import React, { useEffect } from 'react';
import FAQSection from '../components/common/FAQSection';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { RiShieldCheckLine, RiBrainLine, RiLineChartLine, RiBankCardLine, RiFileTextLine, RiSearchEyeLine, RiHistoryLine, RiCompass3Line, RiGridFill } from 'react-icons/ri';
import { SiXero, SiQuickbooks, SiSage, SiSap, SiZoho, SiExpensify, SiIntuit } from 'react-icons/si';
import { FaAws, FaMicrosoft } from 'react-icons/fa';
import { VscAzure } from 'react-icons/vsc';
import { GrOracle } from 'react-icons/gr';
import SEO from '../components/common/SEO';

const HeliBackground = () => {
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 0, pointerEvents: 'none' }}>
      {/* Mesh Gradient Blobs */}
      <motion.div
        animate={{
          x: [0, 100, -50, 0],
          y: [0, -50, 50, 0],
          scale: [1, 1.2, 0.9, 1],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: 'absolute',
          top: '-10%',
          left: '10%',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(37, 99, 235, 0.4) 0%, rgba(37, 99, 235, 0) 70%)',
          filter: 'blur(80px)',
          borderRadius: '50%',
        }}
      />
      <motion.div
        animate={{
          x: [0, -80, 60, 0],
          y: [0, 100, -40, 0],
          scale: [1, 1.1, 1.3, 1],
        }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        style={{
          position: 'absolute',
          bottom: '-10%',
          right: '5%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0) 70%)',
          filter: 'blur(100px)',
          borderRadius: '50%',
        }}
      />
      <motion.div
        animate={{
          x: [0, 40, -90, 0],
          y: [0, -70, 80, 0],
        }}
        transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
        style={{
          position: 'absolute',
          top: '20%',
          right: '15%',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(96, 165, 250, 0.25) 0%, rgba(96, 165, 250, 0) 70%)',
          filter: 'blur(90px)',
          borderRadius: '50%',
        }}
      />
      
      {/* Noise Texture Overlay */}
      <div style={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'url("https://grainy-gradients.vercel.app/noise.svg")', filter: 'contrast(150%) brightness(1000%)' }} />
    </div>
  );
};

const BrainVisualization = () => {
  return (
    <div style={{ position: 'relative', width: '100%', height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {/* Background Neural Network SVG */}
      <svg width="100%" height="100%" viewBox="0 0 400 400" style={{ position: 'absolute', top: 0, left: 0 }}>
        <defs>
          <linearGradient id="grad-prof" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#2563eb" stopOpacity="0.2" />
          </linearGradient>
          <linearGradient id="grad-comp" x1="100%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor="#34d399" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#059669" stopOpacity="0.2" />
          </linearGradient>
        </defs>
        
        {/* Connection Lines (Synapses) */}
        {[...Array(12)].map((_, i) => (
          <motion.line
            key={i}
            x1={100 + Math.random() * 200}
            y1={100 + Math.random() * 200}
            x2={100 + Math.random() * 200}
            y2={100 + Math.random() * 200}
            stroke="rgba(255,255,255,0.1)"
            strokeWidth="1"
            initial={{ opacity: 0.1 }}
            animate={{ opacity: [0.1, 0.4, 0.1] }}
            transition={{ duration: 2 + Math.random() * 2, repeat: Infinity, ease: "easeInOut" }}
          />
        ))}
      </svg>

      {/* Two Brain Hemispheres */}
      <div style={{ display: 'flex', gap: '20px', position: 'relative', zIndex: 1 }}>
        {/* Professional Brain */}
        <motion.div
          animate={{ scale: [1, 1.02, 1], y: [0, -10, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          style={{
            width: '180px',
            height: '240px',
            background: 'rgba(37, 99, 235, 0.15)',
            backdropFilter: 'blur(12px)',
            borderRadius: '100px 50px 50px 100px',
            border: '2px solid rgba(96, 165, 250, 0.4)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            textAlign: 'center',
            boxShadow: '0 0 40px rgba(37, 99, 235, 0.2)'
          }}
        >
          <RiBrainLine size={48} color="#60a5fa" />
          <div style={{ marginTop: '16px', fontSize: '0.8rem', fontWeight: 800, color: 'white', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Professional<br/>Knowledge</div>
          {/* Pulsing Nodes */}
          <motion.div animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2, repeat: Infinity }} style={{ width: '8px', height: '8px', background: '#60a5fa', borderRadius: '50%', position: 'absolute', top: '20%', right: '20%', boxShadow: '0 0 10px #60a5fa' }} />
          <motion.div animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2.5, repeat: Infinity }} style={{ width: '6px', height: '6px', background: '#60a5fa', borderRadius: '50%', position: 'absolute', bottom: '30%', left: '30%', boxShadow: '0 0 10px #60a5fa' }} />
        </motion.div>

        {/* Company Brain */}
        <motion.div
          animate={{ scale: [1, 1.02, 1], y: [0, 10, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
          style={{
            width: '180px',
            height: '240px',
            background: 'rgba(16, 185, 129, 0.15)',
            backdropFilter: 'blur(12px)',
            borderRadius: '50px 100px 100px 50px',
            border: '2px solid rgba(52, 211, 153, 0.4)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            textAlign: 'center',
            boxShadow: '0 0 40px rgba(16, 185, 129, 0.2)'
          }}
        >
          <RiHistoryLine size={48} color="#34d399" />
          <div style={{ marginTop: '16px', fontSize: '0.8rem', fontWeight: 800, color: 'white', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Institutional<br/>Memory</div>
          {/* Pulsing Nodes */}
          <motion.div animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 3, repeat: Infinity }} style={{ width: '8px', height: '8px', background: '#34d399', borderRadius: '50%', position: 'absolute', top: '40%', left: '20%', boxShadow: '0 0 10px #34d399' }} />
          <motion.div animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.8, repeat: Infinity }} style={{ width: '6px', height: '6px', background: '#34d399', borderRadius: '50%', position: 'absolute', bottom: '20%', right: '30%', boxShadow: '0 0 10px #34d399' }} />
        </motion.div>
      </div>

      {/* Central "Nexus" Core */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: 'absolute',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          border: '2px dashed rgba(255,255,255,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2
        }}
      >
        <div style={{ width: '12px', height: '12px', background: 'white', borderRadius: '50%', boxShadow: '0 0 20px white' }} />
      </motion.div>
    </div>
  );
};

const CPA = () => {
  const [windowWidth, setWindowWidth] = React.useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  
  useEffect(() => { 
    window.scrollTo(0, 0); 
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth < 1024;

  const [activeBrain, setActiveBrain] = React.useState('professional');

  // Auto-rotate tabs on mobile
  useEffect(() => {
    if (!isMobile) return;
    const interval = setInterval(() => {
      setActiveBrain(prev => prev === 'professional' ? 'company' : 'professional');
    }, 4000);
    return () => clearInterval(interval);
  }, [isMobile]);

  const professionalCapabilities = [
    { title: 'Financial Reporting', desc: 'IFRS/SFRS(I) expert. Lease treatment (IFRS 16), Revenue (IFRS 15), and Financial Instruments (IFRS 9).', icon: <RiFileTextLine /> },
    { title: 'Tax Strategy', desc: 'Transfer pricing, treaty utilization, holding company optimization, and permanent establishment risk planning.', icon: <RiCompass3Line /> },
    { title: 'Audit Readiness', desc: 'Prepares audit files, management representation letters, and going concern assessments before auditors ask.', icon: <RiSearchEyeLine /> },
    { title: 'Internal Controls', desc: 'COSO framework alignment, segregation of duties (SoD) design, and authorization matrix enforcement.', icon: <RiShieldCheckLine /> },
  ];

  const companyCapabilities = [
    { title: 'Institutional Memory', desc: 'Remembers why that specific invoice was written off in Q2 and the unique reason for that contractor accrual.', icon: <RiHistoryLine /> },
    { title: 'Classification Instinct', desc: 'AWS always goes to 400-Hosting. Grab goes to T&E. Instant classification without manual guessing.', icon: <RiBrainLine /> },
    { title: 'Close Rhythm Awareness', desc: 'Knows your monthly close sequence: bank rec, accruals, depreciation, then GST review by the 10th.', icon: <RiLineChartLine /> },
    { title: 'Anomaly Radar', desc: 'Detects if a bill is 3x the usual amount for a vendor based on 3 years of historical tenure.', icon: <RiSearchEyeLine /> },
  ];

  return (
    <main style={{ paddingTop: '80px', minHeight: '100vh', background: '#fafafa', color: '#111827', fontFamily: '"General Sans", Inter, sans-serif' }}>
      <SEO pathname="/agents/cpa" agentKey="CPA" />

      {/* HERO SECTION */}
      <section style={{ padding: '80px 0 100px', background: '#0f172a', color: 'white', position: 'relative', overflow: 'hidden' }}>
        <HeliBackground />
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '64px', alignItems: 'center' }}>
            <div style={{ flex: '1.2 1 0%', minWidth: '320px' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 14px', background: 'rgba(255,255,255,0.1)', borderRadius: '999px', border: '1px solid rgba(255,255,255,0.2)', marginBottom: '24px', fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                <span style={{ width: '8px', height: '8px', background: '#34d399', borderRadius: '50%', boxShadow: '0 0 10px #34d399' }} />
                Autonomous CPA Agent
              </div>
              <h1 style={{ fontSize: '3.8rem', fontWeight: 900, lineHeight: 1.05, marginBottom: '24px', letterSpacing: '-0.04em', color: '#ffffff' }}>
                Professional Judgment. <br />
                <span style={{ color: '#60a5fa' }}>Institutional Memory.</span>
              </h1>
              <p style={{ fontSize: '1.25rem', color: '#cbd5e1', lineHeight: 1.6, marginBottom: '40px', maxWidth: '580px' }}>
                Deploy an agent with the technical depth of a CPA and the company knowledge of a 3-year tenure accountant. From IFRS 16 compliance to autonomous vendor classification.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                <a href="https://app.eagentix.com/register" target="_blank" rel="noopener noreferrer" style={{ padding: '16px 32px', background: '#0071e3', color: 'white', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', boxShadow: '0 10px 25px -5px rgba(0,113,227,0.4)' }}>
                  Onboard Your Agent
                </a>
              </div>

              {/* INTEGRATION ECOSYSTEM */}
              <div style={{ marginTop: '48px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', color: '#93c5fd' }}>
                  <RiGridFill size={20} />
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Native Integration to 50+ Financial Tools</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'center' }}>
                  <SiXero size={32} color="#ffffff" title="Xero" style={{ opacity: 0.9 }} />
                  <SiQuickbooks size={32} color="#ffffff" title="QuickBooks" style={{ opacity: 0.9 }} />
                  <GrOracle size={32} color="#ffffff" title="NetSuite" style={{ opacity: 0.9 }} />
                  <SiSap size={36} color="#ffffff" title="SAP" style={{ opacity: 0.9 }} />
                  <SiSage size={32} color="#ffffff" title="Sage" style={{ opacity: 0.9 }} />
                  <FaMicrosoft size={32} color="#ffffff" title="Microsoft Dynamics" style={{ opacity: 0.9 }} />
                  <SiZoho size={32} color="#ffffff" title="Zoho Books" style={{ opacity: 0.9 }} />
                  <SiExpensify size={32} color="#ffffff" title="Expensify" style={{ opacity: 0.9 }} />
                  <SiIntuit size={32} color="#ffffff" title="Intuit" style={{ opacity: 0.9 }} />
                  <div style={{ width: '1px', height: '24px', background: 'rgba(255,255,255,0.2)' }} />
                  <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#ffffff', opacity: 0.6 }}>+ MANY MORE</span>
                </div>
              </div>
            </div>
            <div style={{ flex: '1 1 0%', minWidth: '320px', perspective: '1000px' }}>
              <BrainVisualization />
            </div>
          </div>
        </div>
      </section>

      {/* THE TWO BRAINS VISUALIZATION */}
      <section style={{ padding: isMobile ? '60px 0' : '100px 0', background: 'white' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <span style={{ color: '#0071e3', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.2em', fontSize: '0.75rem' }}>Core Intelligence Architecture</span>
            <h2 style={{ fontSize: isMobile ? '2.2rem' : '3.2rem', fontWeight: 900, color: '#0f172a', marginTop: '16px', letterSpacing: '-0.03em', lineHeight: 1.1 }}>The Two Brains of a Senior CPA</h2>
            <p style={{ fontSize: '1.2rem', color: '#475569', maxWidth: '750px', margin: '20px auto 0' }}>Most agents start cold. The Eagentix CPA combines technical mastery with deep operational context learned over years.</p>
          </div>

          {!isMobile ? (
            /* Desktop: Show Both Side-by-Side */
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
              {/* Professional Column */}
              <div style={{ background: '#f8fafc', padding: '40px', borderRadius: '32px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
                  <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: '#0071e3', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem' }}>
                    <RiBrainLine />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', margin: 0 }}>The Professional Brain</h3>
                    <div style={{ color: '#0071e3', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase' }}>Technical Mastery</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gap: '20px' }}>
                  {professionalCapabilities.map((cap, i) => (
                    <div key={i} style={{ background: 'white', padding: '24px', borderRadius: '20px', border: '1px solid #f1f5f9', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <span style={{ color: '#0071e3', fontSize: '1.2rem' }}>{cap.icon}</span>
                        <h4 style={{ margin: 0, fontWeight: 800, color: '#1e293b' }}>{cap.title}</h4>
                      </div>
                      <p style={{ margin: 0, color: '#64748b', fontSize: '0.95rem', lineHeight: 1.5 }}>{cap.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Company Column */}
              <div style={{ background: '#f1fdf9', padding: '40px', borderRadius: '32px', border: '1px solid #d1fae5' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
                  <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: '#34d399', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem' }}>
                    <RiHistoryLine />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#064e3b', margin: 0 }}>The Company Brain</h3>
                    <div style={{ color: '#059669', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase' }}>Institutional Memory</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gap: '20px' }}>
                  {companyCapabilities.map((cap, i) => (
                    <div key={i} style={{ background: 'white', padding: '24px', borderRadius: '20px', border: '1px solid #ecfdf5', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <span style={{ color: '#34d399', fontSize: '1.2rem' }}>{cap.icon}</span>
                        <h4 style={{ margin: 0, fontWeight: 800, color: '#064e3b' }}>{cap.title}</h4>
                      </div>
                      <p style={{ margin: 0, color: '#475569', fontSize: '0.95rem', lineHeight: 1.5 }}>{cap.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Mobile: Tabbed View with Auto-Rotation */
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', background: '#f8fafc', padding: '8px', borderRadius: '16px', marginBottom: '32px' }}>
                <button 
                  onClick={() => setActiveBrain('professional')}
                  style={{ padding: '12px', borderRadius: '12px', border: 'none', background: activeBrain === 'professional' ? '#0071e3' : 'transparent', color: activeBrain === 'professional' ? 'white' : '#64748b', fontWeight: 700, fontSize: '0.9rem', transition: '0.2s' }}
                >
                  Professional
                </button>
                <button 
                  onClick={() => setActiveBrain('company')}
                  style={{ padding: '12px', borderRadius: '12px', border: 'none', background: activeBrain === 'company' ? '#34d399' : 'transparent', color: activeBrain === 'company' ? 'white' : '#64748b', fontWeight: 700, fontSize: '0.9rem', transition: '0.2s' }}
                >
                  Memory
                </button>
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeBrain}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  {(activeBrain === 'professional' ? professionalCapabilities : companyCapabilities).map((cap, i) => (
                    <div key={i} style={{ padding: '24px', background: '#ffffff', borderRadius: '20px', border: `1px solid ${activeBrain === 'professional' ? '#e2e8f0' : '#d1fae5'}`, marginBottom: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                      <div style={{ fontSize: '1.8rem', color: activeBrain === 'professional' ? '#0071e3' : '#34d399', marginBottom: '16px' }}>{cap.icon}</div>
                      <h3 style={{ fontWeight: 800, fontSize: '1.2rem', color: '#0f172a', marginBottom: '8px' }}>{cap.title}</h3>
                      <p style={{ color: '#475569', lineHeight: 1.5, fontSize: '0.95rem', margin: 0 }}>{cap.desc}</p>
                    </div>
                  ))}
                </motion.div>
              </AnimatePresence>
            </>
          )}
        </div>
      </section>

      {/* ARTIFACTS SECTION */}
      <section style={{ padding: '100px 0', background: '#f8fafc' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '64px', alignItems: 'center' }}>
            <div style={{ flex: '1', minWidth: '400px' }}>
              <span style={{ color: '#0071e3', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.2em', fontSize: '0.75rem' }}>Autonomous Deliverables</span>
              <h2 style={{ fontSize: '2.8rem', fontWeight: 900, color: '#0f172a', marginTop: '16px', letterSpacing: '-0.03em', lineHeight: 1.1 }}>Engineering-Grade Accounting Artifacts</h2>
              <div style={{ marginTop: '40px', display: 'grid', gap: '24px' }}>
                {[
                  { title: 'Audit-Ready Lead Schedules', desc: 'Complete working papers for all material BS lines with roll-forwards.' },
                  { title: 'IFRS 15 Revenue Analysis', desc: '5-step recognition model applied to complex customer contracts.' },
                  { title: 'Prepayment & Accrual Engine', desc: 'Auto-tracking schedules that release and catch-up every month.' },
                  { title: 'BvA Management Packs', desc: 'Board-ready packs with variance analysis explained by institutional memory.' }
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: '16px' }}>
                    <div style={{ flexShrink: 0, width: '24px', height: '24px', borderRadius: '50%', background: '#e0f2fe', color: '#0071e3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 800 }}>{i+1}</div>
                    <div>
                      <h4 style={{ fontWeight: 800, fontSize: '1.1rem', color: '#0f172a', margin: '0 0 4px' }}>{item.title}</h4>
                      <p style={{ color: '#475569', fontSize: '0.95rem', margin: 0 }}>{item.item ? item.item : item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ flex: '1.2', minWidth: '400px' }}>
              <div id="artifact-container" style={{ position: 'relative' }}>
                {/* Visualizing a Close Checklist Artifact */}
                <div style={{ background: 'white', borderRadius: '24px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 30px 60px -15px rgba(0,0,0,0.1)' }}>
                  <div style={{ padding: '24px', background: '#0f172a', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', opacity: 0.6, letterSpacing: '0.1em' }}>Month-End Artifact</div>
                      <div style={{ fontSize: '1.2rem', fontWeight: 800 }}>Close Calendar: May 2026</div>
                    </div>
                    <div style={{ background: 'rgba(52,211,153,0.15)', color: '#34d399', padding: '6px 12px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 800 }}>78% COMPLETE</div>
                  </div>
                  <div style={{ padding: '24px' }}>
                    {[
                      { task: 'Bank Reconciliation (DBS Operating)', status: 'Verified', time: 'D+1', color: '#34d399' },
                      { task: 'IFRS 16 Lease Amortization', status: 'Drafted', time: 'D+2', color: '#60a5fa' },
                      { task: 'AWS Recurring Accrual (Est. $12.4K)', status: 'Awaiting Sign-off', time: 'D+4', color: '#fbbf24' },
                      { task: 'GST Reconciliation (Q2)', status: 'In Progress', time: 'D+5', color: '#94a3b8' }
                    ].map((t, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 0', borderBottom: i === 3 ? 'none' : '1px solid #f1f5f9' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: t.color }} />
                          <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>{t.task}</span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: 800, color: t.color }}>{t.status}</div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Due {t.time}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding: '20px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
                    <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>Action: <u style={{ color: '#0071e3', cursor: 'pointer' }}>Generate Working Papers for Review</u></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RISK & GOVERNANCE */}
      <section style={{ padding: '100px 0', background: 'white' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ background: '#0f172a', borderRadius: '40px', padding: isMobile ? '40px 24px' : '80px', color: 'white', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: '-100px', right: '-100px', width: '300px', height: '300px', background: '#3b82f6', filter: 'blur(100px)', opacity: 0.2 }} />
            
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1.5fr', gap: '64px', position: 'relative', zIndex: 1 }}>
              <div>
                <h2 style={{ fontSize: '2.8rem', fontWeight: 900, marginBottom: '24px', letterSpacing: '-0.03em', color: 'white', lineHeight: 1.1 }}>Deterministic Safety Model</h2>
                <p style={{ fontSize: '1.2rem', color: '#93c5fd', lineHeight: 1.6, marginBottom: '40px' }}>Every GL mutation follows a strict 7-tier risk profile. The agent never "guesses" with your general ledger.</p>
                
                <div style={{ display: 'grid', gap: '24px' }}>
                  {[
                    { title: 'Lock Period Enforcement', desc: 'Automatic block on any posting dated ≤ the last closed period. Prevents historical data tampering.' },
                    { title: 'Audit Trail Contract', desc: 'Every mutation generates a before/after snapshot, identity log, and autonomous reversal instructions.' },
                    { title: 'Confidence Gating', desc: 'Autonomous classification only for >0.9 confidence; everything else triggers a human review cycle.' }
                  ].map((f, i) => (
                    <div key={i} style={{ paddingLeft: '20px', borderLeft: '3px solid #3b82f6' }}>
                      <div style={{ fontWeight: 800, fontSize: '1.1rem', marginBottom: '4px' }}>{f.title}</div>
                      <div style={{ color: '#94a3b8', fontSize: '0.95rem', lineHeight: 1.5 }}>{f.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '32px', border: '1px solid rgba(255,255,255,0.1)', padding: isMobile ? '24px' : '40px' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 800, color: '#60a5fa', textTransform: 'uppercase', marginBottom: '24px', letterSpacing: '0.1em' }}>7-Tier Action Risk Profile</div>
                <div style={{ display: 'grid', gap: '12px' }}>
                  {[
                    { tier: 1, label: 'Read-Only Querying', mode: 'Autonomous', risk: 'Safe' },
                    { tier: 2, label: 'Draft Postings', mode: 'Autonomous', risk: 'Low' },
                    { tier: 3, label: 'Classification Suggestions', mode: 'Confidence Gated', risk: 'Low' },
                    { tier: 4, label: 'Approval Transitions', mode: 'Human Confirmation', risk: 'Medium' },
                    { tier: 5, label: 'Direct GL Mutation', mode: 'Always Confirmed', risk: 'Medium' },
                    { tier: 6, label: 'Payment & Cash Movement', mode: 'MFA Verified', risk: 'High' },
                    { tier: 7, label: 'Destructive (Void/Write-off)', mode: 'Explicit Authorization', risk: 'High' }
                  ].map((t) => (
                    <div key={t.tier} style={{ display: 'grid', gridTemplateColumns: '40px 1fr auto', alignItems: 'center', gap: '16px', padding: '12px 16px', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#475569' }}>{t.tier}</div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{t.label}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{t.mode}</div>
                      </div>
                      <div style={{ 
                        fontSize: '0.65rem', 
                        fontWeight: 900, 
                        padding: '4px 8px', 
                        borderRadius: '4px',
                        background: t.risk === 'Safe' ? '#059669' : t.risk === 'Low' ? '#3b82f6' : t.risk === 'Medium' ? '#d97706' : '#dc2626',
                        color: 'white',
                        textTransform: 'uppercase'
                      }}>
                        {t.risk}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section style={{ padding: '100px 0', background: '#fafafa' }}>
        <div className="container" style={{ maxWidth: '800px', margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.5rem', fontWeight: 900, color: '#0f172a', marginBottom: '24px', letterSpacing: '-0.03em' }}>Ready for a 24/7 Virtual CPA?</h2>
          <p style={{ fontSize: '1.2rem', color: '#475569', marginBottom: '40px' }}>Stop waiting for month-end close. Start operating with continuous, autonomous accounting intelligence.</p>
          <a href="https://app.eagentix.com/register" style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', padding: '18px 40px', background: '#0071e3', color: 'white', borderRadius: '14px', fontWeight: 800, textDecoration: 'none', fontSize: '1.1rem' }}>
            Request Platform Access
            <RiFileTextLine />
          </a>
        </div>
      </section>
      <FAQSection agentKey="CPA" />
    </main>
  );
};

export default CPA;
